import { useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { SimpleButton, ErrorMessage } from '@/src/ui';
import {
    ControlledDynamicTextEditor,
    ControlledDynamicEquationEditor,
    isEditorContentEmpty,
    OpenTextRenderer,
    type QuestionMetaDTO,
    type QtiOpenTextQuestionDTO,
} from '@/src/lib';
import {
    Debugger,
    QuestionMetaForm,
    TextEntryModal,
    QuestionTitleField,
    type TextEntryModalPartialProps,
} from '../_shared';
import { defaultMeta } from '../../models/questionDTOs';
import { getChoiceSchemaDEE, metaSchema, areItemsUnique } from '../../models/schema';
import { renderLoaders } from '../../utils';
import { saveQuestion } from '../../services';
import { Constants } from '@/src/utils';
const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

const config = {
    maxAnswers: 10,
};

// Validation schema
const schema = yup.object().shape({
    id: yup.number().optional(),
    title: yup
        .string()
        .trim()
        .required('Title is required')
        .max(
            Constants.MAX_CHARS.TITLE,
            `Title cannot exceed ${Constants.MAX_CHARS.TITLE} characters`
        ),
    prompt: yup
        .string()
        .test(
            'not-empty',
            'Prompt cannot be empty.',
            (value) => !isEditorContentEmpty(value || '')
        ),
    prompt_es: yup.string().when('$isSpanishAllowed', {
        is: true,
        then: (schema) =>
            schema
                .test(
                    'not-empty',
                    'Prompt (Spanish) cannot be empty.',
                    (value) => !isEditorContentEmpty(value || '')
                )
                .required('Prompt (Spanish) is required'),
        otherwise: (schema) => schema.optional(),
    }),
    correctAnswers: yup
        .array()
        .of(getChoiceSchemaDEE('Answer text'))
        .required()
        .min(1, `At least 1 choice is required`)
        .max(config.maxAnswers, `A maximum of ${config.maxAnswers} choices is allowed`)
        .test('unique', 'Choices must be unique.', (choiceList) =>
            areItemsUnique(choiceList, 'text')
        )
        .required('Choices are required')
        .when('$isSpanishAllowed', {
            is: true,
            then: (schema) =>
                schema.test('unique', 'Spanish choices must be unique.', (choiceList) =>
                    areItemsUnique(choiceList, 'text_es')
                ),
            otherwise: (schema) => schema,
        }),
    meta: metaSchema.required(),
});

interface OpenTextEditorProps {
    initialModel?: QtiOpenTextQuestionDTO;
}

export default function OpenTextEditor({ initialModel }: OpenTextEditorProps) {
    const isEdit = !!initialModel;
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isTitleError, setIsTitleError] = useState(false);
    const [modalProps, setModalProps] = useState<TextEntryModalPartialProps | null>(null);

    const defaultValues: QtiOpenTextQuestionDTO = initialModel
        ? initialModel
        : {
              title: '',
              prompt: '',
              prompt_es: '',
              correctAnswers: [],
              meta: { ...defaultMeta, questionTypeMasterId: qtm.OPEN_TEXT },
          };

    // For some reason, react hook form doesn't like it if we just use an array of strings
    // So, this convoluted implementation is necessary
    const [nextAnswerId, setNextAnswerId] = useState(
        initialModel && initialModel.correctAnswers?.length
            ? Math.max(0, ...initialModel.correctAnswers.map((a: any) => a.id ?? 0)) + 1
            : 1
    );

    const [formContext, setFormContext] = useState({
        isSpanishAllowed: defaultValues.meta.isSpanishAllowed,
    });

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting },
        setError: setFormError,
        clearErrors,
    } = useForm<QtiOpenTextQuestionDTO>({
        defaultValues,
        resolver: yupResolver(schema as any),
        mode: 'onBlur',
        context: formContext,
    });

    const question = watch();
    useEffect(() => {
        setFormContext({ isSpanishAllowed: question.meta.isSpanishAllowed });
    }, [question.meta.isSpanishAllowed]);

    const { append, remove } = useFieldArray({
        control,
        name: 'correctAnswers',
    });
    const correctAnswers = watch('correctAnswers');

    const handleAddCorrectAnswer = () => {
        if (correctAnswers.length >= config.maxAnswers) return;
        append({
            id: nextAnswerId,
            text: Constants.DEE_DEFAULT_TEXT,
            text_es: Constants.DEE_DEFAULT_TEXT,
        });
        setNextAnswerId((prev) => prev + 1);
    };

    const handleLanguageSet = useCallback(
        (language: 'en' | 'es') => {
            if (language === 'en') return;

            if (!question.prompt_es) {
                setValue('prompt_es', question.prompt);
            }
            question.correctAnswers.forEach((answer, index) => {
                if (!answer.text_es) {
                    setValue(`correctAnswers.${index}.text_es`, answer.text);
                }
            });
        },
        [question, setValue]
    );

    const onSubmit = async () => {
        if (success) return;
        if (isTitleError) {
            setError('Please choose a unique title.');
            return;
        }

        clearErrors();
        setLoading(true);
        setError(null);
        try {
            const viewMode = isEdit ? 'edit' : 'create';
            await saveQuestion(question, viewMode);
            setSuccess(true);
        } catch (e: any) {
            setError(e.message || 'Unknown error');
            setFormError('root', { type: 'manual', message: e.message || 'Unknown error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            key={`OpenTextEditor-${question.meta.isSpanishAllowed}`}
            className="p-4 space-y-4"
            onSubmit={handleSubmit(onSubmit)}
        >
            {/* Handles all Spanish entry */}
            {modalProps && (
                <TextEntryModal
                    initialData={modalProps.initialData}
                    currentValue={watch(modalProps.fieldName as any)}
                    fieldName={modalProps.fieldName}
                    friendlyName={modalProps.friendlyName}
                    textMode={modalProps.textMode}
                    showKeypad={question.meta.shouldShowKeypad}
                    allowedKeys={question.meta.keypadSetting}
                    error={modalProps.error}
                    control={control}
                    handleClose={() => setModalProps(null)}
                />
            )}
            {renderLoaders(loading, success, error, isEdit)}

            <h2>Open Text Question Title:</h2>
            <QuestionTitleField
                id={question.id}
                title={question.title}
                control={control}
                setIsTitleError={setIsTitleError}
                error={errors.title}
            />

            <div className="my-4" />

            <div className="flex flex-row gap-4">
                <h2>Prompt:</h2>
                {question.meta.isSpanishAllowed && (
                    <div
                        onClick={() =>
                            setModalProps({
                                initialData: question.prompt_es,
                                fieldName: 'prompt_es',
                                friendlyName: 'Prompt (Spanish)',
                                textMode: 'rich',
                                error: errors.prompt_es?.message,
                            })
                        }
                        style={{ cursor: 'pointer' }}
                    >
                        Edit Spanish
                    </div>
                )}
                <ErrorMessage error={errors.prompt_es} />
            </div>

            <ControlledDynamicTextEditor
                name="prompt"
                control={control}
                initialValue={question.prompt}
                style={{ minHeight: '150px' }}
            />
            <ErrorMessage error={errors.prompt} />

            <div className="my-4" />

            <h2>Correct Answers:</h2>
            {correctAnswers.map((answer, index) => {
                return (
                    <div key={`ans-${answer.id}`}>
                        <div className="flex items-center gap-6 mb-2">
                            <div>
                                {question.meta.isSpanishAllowed && (
                                    <div
                                        onClick={() =>
                                            setModalProps({
                                                initialData: answer.text_es,
                                                fieldName: `correctAnswers.${index}.text_es`,
                                                friendlyName: `Answer (Spanish)`,
                                                textMode: 'equation',
                                                error: errors.correctAnswers?.[index]?.text_es
                                                    ?.message,
                                            })
                                        }
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Edit Spanish
                                    </div>
                                )}
                                <ErrorMessage error={errors.correctAnswers?.[index]?.text_es} />
                                <ControlledDynamicEquationEditor
                                    name={`correctAnswers.${index}.text`}
                                    control={control}
                                    initialValue={answer.text}
                                    contentKey={`${question.id}:${answer.id}`}
                                    style={{ minWidth: '300px' }}
                                    showKeypad={question.meta.shouldShowKeypad}
                                    allowedKeys={question.meta.keypadSetting}
                                />
                                {/* LIMIT 2000 because quotes get escaped & db limit is 3000 */}
                                <div>{answer.text.length}/2000 compressed length</div>
                            </div>
                            <SimpleButton
                                type="button"
                                color="error"
                                variant="outlined"
                                onClick={() => remove(index)}
                            >
                                Remove
                            </SimpleButton>
                        </div>
                        <ErrorMessage error={errors.correctAnswers?.[index]?.text} />
                    </div>
                );
            })}
            <ErrorMessage error={errors.correctAnswers?.root} />
            <ErrorMessage error={errors.correctAnswers} />

            <SimpleButton
                type="button"
                color="primary"
                onClick={() => handleAddCorrectAnswer()}
                className="bg-blue-500 text-white px-3 py-1 rounded"
            >
                Add Answer
            </SimpleButton>

            <div className="my-4" />

            <QuestionMetaForm
                rhfProps={{
                    control,
                    errors: errors.meta,
                    watch,
                }}
                handleLanguageSet={handleLanguageSet}
                disableList={['shouldShuffleOptions']}
                metaValues={watch('meta')}
                setMetaValues={(meta: QuestionMetaDTO) => setValue('meta', meta)}
            />

            <div className="border rounded p-4 bg-white max-w-4xl space-y-4 mt-4">
                <h4 className="font-semibold mb-1">Interactive Preview</h4>
                <OpenTextRenderer
                    question={question}
                    options={{
                        showLanguageToggle: question.meta.isSpanishAllowed,
                        showScoring: true,
                    }}
                />
            </div>

            <div className="flex items-center space-x-2">
                <div>
                    <SimpleButton
                        type="submit"
                        color="primary"
                        className="bg-green-600 text-white px-3 py-1 rounded text-xs"
                        disabled={isSubmitting || loading}
                    >
                        {isEdit ? 'Update Question' : 'Create Question'}
                    </SimpleButton>
                </div>
            </div>
            <ErrorMessage
                error={Object.keys(errors).length ? 'Please correct the errors to continue' : ''}
            />
            {renderLoaders(loading, success, error, isEdit)}

            <Debugger question={question} errors={errors} />
        </form>
    );
}
